import React, { useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const COUNTRIES = [
  ["FR", "France"], ["BE", "Belgique"], ["LU", "Luxembourg"], ["CH", "Suisse"],
  ["DE", "Allemagne"], ["ES", "Espagne"], ["IT", "Italie"], ["PT", "Portugal"],
  ["NL", "Pays-Bas"], ["IE", "Irlande"], ["US", "Etats-Unis"], ["Other", "Autre"],
];

export default function Qualification() {
  const { t } = useI18n();
  const { checkAuth } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [a, setA] = useState({
    residence_country: "FR",
    investor_type: "retail",
    net_worth_range: "50k-250k",
    experience_years: 1,
    risk_tolerance: "medium",
    horizon_accepted: false,
    loss_accepted: false,
    liquidity_accepted: false,
    pep_status: false,
    source_of_funds: "salary",
  });
  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/qualification", a);
      await checkAuth();
      if (data.eligible) {
        toast.success(data.message);
        nav("/invest");
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur qualification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="section border-b border-slate-200">
        <div className="container-page">
          <div className="label-divider mb-6">{t.qualification.title}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-slate-900" data-testid="qualification-title">
            {t.qualification.title}
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">{t.qualification.subtitle}</p>
        </div>
      </section>

      <section className="section">
        <div className="max-w-3xl mx-auto px-6">
          <form onSubmit={submit} className="space-y-10 bg-white border border-slate-200 rounded-sm p-8" data-testid="qualification-form">

            <Row label={t.qualification.country}>
              <Select value={a.residence_country} onValueChange={(v) => set("residence_country", v)}>
                <SelectTrigger className="rounded-sm" data-testid="q-country"><SelectValue /></SelectTrigger>
                <SelectContent>{COUNTRIES.map(([v, n]) => <SelectItem key={v} value={v}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </Row>

            <Row label={t.qualification.investor_type}>
              <RadioGroup value={a.investor_type} onValueChange={(v) => set("investor_type", v)} className="grid grid-cols-3 gap-2">
                {[["retail", "Particulier"], ["qualified", "Qualifié"], ["professional", "Professionnel"]].map(([k, l]) => (
                  <label key={k} className={`cursor-pointer border rounded-sm p-3 text-center ${a.investor_type === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`} data-testid={`q-type-${k}`}>
                    <RadioGroupItem value={k} className="sr-only" />
                    <div className="text-sm">{l}</div>
                  </label>
                ))}
              </RadioGroup>
            </Row>

            <Row label={t.qualification.experience}>
              <Input type="number" min={0} max={40} value={a.experience_years} onChange={(e) => set("experience_years", Number(e.target.value))} className="rounded-sm font-mono w-32" data-testid="q-experience" />
            </Row>

            <Row label={t.qualification.net_worth}>
              <Select value={a.net_worth_range} onValueChange={(v) => set("net_worth_range", v)}>
                <SelectTrigger className="rounded-sm" data-testid="q-net-worth"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="<50k">&lt; 50 000€</SelectItem>
                  <SelectItem value="50k-250k">50 000€ – 250 000€</SelectItem>
                  <SelectItem value="250k-1m">250 000€ – 1 000 000€</SelectItem>
                  <SelectItem value=">1m">&gt; 1 000 000€</SelectItem>
                </SelectContent>
              </Select>
            </Row>

            <Row label={t.qualification.risk_tolerance}>
              <RadioGroup value={a.risk_tolerance} onValueChange={(v) => set("risk_tolerance", v)} className="grid grid-cols-3 gap-2">
                {[["low", "Faible"], ["medium", "Modérée"], ["high", "Élevée"]].map(([k, l]) => (
                  <label key={k} className={`cursor-pointer border rounded-sm p-3 text-center ${a.risk_tolerance === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`} data-testid={`q-risk-${k}`}>
                    <RadioGroupItem value={k} className="sr-only" />
                    <div className="text-sm">{l}</div>
                  </label>
                ))}
              </RadioGroup>
            </Row>

            <Row label={t.qualification.source_of_funds}>
              <Select value={a.source_of_funds} onValueChange={(v) => set("source_of_funds", v)}>
                <SelectTrigger className="rounded-sm" data-testid="q-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salary">Salaire / revenus professionnels</SelectItem>
                  <SelectItem value="business">Dividendes / activité</SelectItem>
                  <SelectItem value="savings">Épargne constituée</SelectItem>
                  <SelectItem value="inheritance">Héritage / donation</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </Row>

            <div className="space-y-4 border-t border-slate-200 pt-6">
              <Acceptance label={t.qualification.horizon_accepted} checked={a.horizon_accepted} onChange={(v) => set("horizon_accepted", v)} testid="q-horizon" />
              <Acceptance label={t.qualification.loss_accepted} checked={a.loss_accepted} onChange={(v) => set("loss_accepted", v)} testid="q-loss" />
              <Acceptance label={t.qualification.liquidity_accepted} checked={a.liquidity_accepted} onChange={(v) => set("liquidity_accepted", v)} testid="q-liquidity" />
              <Acceptance label={t.qualification.pep_status} checked={a.pep_status} onChange={(v) => set("pep_status", v)} testid="q-pep" />
            </div>

            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11" data-testid="qualification-submit">
              {loading ? "…" : t.qualification.submit}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-3 block font-bold">{label}</Label>
      {children}
    </div>
  );
}

function Acceptance({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-800 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} className="mt-0.5" data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}
